import { useCallback, useEffect, useRef, useState } from 'react';
import insertCardImage from '../../assets/InsertCard.svg';
import style from './style.module.scss';
import MessageBoxSet from '../message-box-set';
import { useStore } from '../../lib/hooks/useStore';
import { ViewState, PaymentError } from '../../lib/types/paymentCardState';
import Confirm from '../confirm';
import MainMessageBox from '../../features/main-message-box/main-message-box';
import { AppIconType } from '../../lib/typegen/app-icon-type';
import { useMagTekCardReader } from '../../lib/hooks/useMagtekReader';
import MagtekCardReaderCardData from '../../lib/devices/card-reader/MagtekCardReaderCardData';
import logger from '../../lib/helpers/logger';
import { CISConfigType } from '../../lib/typegen/cis-config-type';
import { PPConfigType } from '../../lib/typegen/pp-config-type';
import api from '../../lib/api/api';
import { PayWithMagTekDataRequest } from '../../lib/typegen/pay-with-mag-tek-data-request';
import { LegacyPostAcceptedPayment, LegacyPostRejectedPayment } from '../../lib/types/legacyAdminApi';
import passKeyHelper from '../../lib/helpers/passKey';
import { v4 as guid } from 'uuid';
import BalancesKvpList from '../balances-kvp-list';
import { PaymentCardServerProps } from '../../lib/typegen/payment-card-server-props';
import { PaymentType } from '../../lib/typegen/payment-type';
import enumHelper from '../../lib/helpers/enums';
import { PayWithMagTekDataResponse } from '../../lib/typegen/pay-with-mag-tek-data-response';
import MessageBox from '../message-box';
import ServiceFeeView from '../service-fee-view';
import { parseAmountToNumber } from '../../lib/helpers/math';
import { PaymentFeeViewServerProps } from '../../lib/typegen/payment-fee-view-server-props';

function PaymentCardMagTek({
	imageAltKey,
	paymentTotalLabelKey,
	infoBoxLabelKey,
	warningBoxLabelKey,
	cardErrorGenericLabelKey,
	processingPaymentLabelKey,
	tryAgainMessageLabelKey,
	tryAgainNoLabelKey,
	tryAgainYesLabelKey,
	previousPageTarget,
	paymentFeeViewServerProps,
}: PaymentCardServerProps) {
	const magtekData = useMagTekCardReader();
	const [currentState, setCurrentState] = useState<ViewState>(ViewState.PAY);
	const [error, setError] = useState<PaymentError | null>(null);
	const [errorHint, setErrorHint] = useState<string | null>(null);
	const [hasProcessed, setHasProcessed] = useState<boolean>(false);
	const [isPreparingTransactionRequired, setIsPreparingTransactionRequired] = useState<boolean>(false);

	const {
		accountStore: {
			cartItems,
			setPaymentType,
			cartTotalAmount,
			transactionFee,
			setTransactionFee,
			setCartId,
			setToken,
			hasPreparedTransaction,
			setHasPreparedTransaction,
			payorInformation,
		},
		uiStore: { handleNavigation },
		kioskStore: { kioskId, companyId, paymentReferenceId },
		sessionStore: { legacyId },
		configStore: { getString, cisType, ppType, kioskLocation },
	} = useStore();
	const isPayGoCustomer = cisType === CISConfigType.PayGo || ppType === PPConfigType.PayGo;

	useEffect(() => setPaymentType(PaymentType.Card), [setPaymentType]);

	const prevStateRef = useRef<ViewState | null>(null);
	useEffect(() => {
		if (prevStateRef.current !== currentState) {
			logger.logGeneral.log({}, `View changed to: ${currentState}`, currentState);
			prevStateRef.current = currentState;
		}
	}, [currentState]);

	const prepareTransaction = useCallback(async () => {
		if (!isPreparingTransactionRequired) {
			logger.debug.log('Prepare transaction not required for this PP config, skipping...');
			return;
		}

		// Check store flag instead of ref
		if (hasPreparedTransaction) {
			logger.debug.log('Transaction already prepared (from store), skipping...');
			return;
		}

		// Set flag immediately to prevent race conditions
		setHasPreparedTransaction(true);

		const prepareTransactionRequest = (() => {
			switch (ppType) {
				case PPConfigType.GrantStreet:
					return {
						accountsToPay: cartItems.map((item) => ({
							accountNumber: item.item.itemNumber,
							amount: item.amountToPay,
						})),
						paymentType: 'CREDIT',
						location: kioskLocation,
						totalAmount: cartTotalAmount,
						payorInfo: payorInformation,
					};
				case PPConfigType.InvoiceCloudV1:
					return {
						accountsToPay: cartItems.map((item) => ({
							accountNumber: item.item.itemNumber,
							amount: item.amountToPay,
							invoiceGuid: item.item.itemGuid,
							icCcPaymentId: item.item.icCcPaymentId,
							creditTypeId: item.item.creditCardTypeId,
						})),
						paymentType: 'CREDIT',
						location: '',
						totalAmount: cartTotalAmount,
						payorInfo: payorInformation,
					};
			}
		})();

		if (!prepareTransactionRequest) {
			logger.logGeneral.error({}, 'No prepare transaction request could be constructed', 'No prepare transaction request could be constructed');
			throw new Error('No prepare transaction request could be constructed');
		}

		try {
			const data = await api.payments.prepareTransaction(prepareTransactionRequest);
			const fees = data?.fees?.creditCard?.fees;

			if (data?.cartId) {
				setCartId(data.cartId);
				setToken(data.token!);
			}

			if (fees) {
				setTransactionFee(fees);
				setCurrentState(ViewState.SERVICE_FEE);
			}
		} catch (error) {
			logger.debug.error('Failed to prepare transaction:', error);
			// Reset the flag on error so it can be retried
			setHasPreparedTransaction(false);
		}
	}, [
		isPreparingTransactionRequired,
		hasPreparedTransaction,
		setHasPreparedTransaction,
		ppType,
		cartItems,
		kioskLocation,
		cartTotalAmount,
		payorInformation,
		setCartId,
		setToken,
		setTransactionFee,
	]);

	useEffect(() => {
		logger.debug.log('ppType:', ppType);
		switch (ppType) {
			case PPConfigType.GrantStreet:
				setIsPreparingTransactionRequired(true);
				break;
			case PPConfigType.InvoiceCloudV1:
				setIsPreparingTransactionRequired(true);
				prepareTransaction();
				break;
			default:
				setIsPreparingTransactionRequired(false);
				break;
		}
	}, [ppType, prepareTransaction]);

	const handlePayment = useCallback(
		async (data: MagtekCardReaderCardData) => {
			if (isPreparingTransactionRequired && !hasPreparedTransaction) return;

			let response: PayWithMagTekDataResponse;
			const item = cartItems[0];
			if (!item) return;
			const fee = transactionFee ? parseAmountToNumber(transactionFee) : 0;
			try {
				setCurrentState(ViewState.PROCESSING);
				setHasProcessed(true);
				const request: PayWithMagTekDataRequest = {
					utilityAccountNumber: item!.item.itemNumber,
					amountToPay: item!.amountToPayWithFee,
					amountDue: item!.amountToPay,
					isPrePay: isPayGoCustomer ? item.item.isPrePay : undefined,
					cardData: data,
					customerName: item!.item.customerName ?? item!.customer?.name ?? '',
					invoiceGuid: item.item.itemGuid,
					paymentTypeId: item.item.icCcPaymentId,
					creditCardTypeId: item.item.creditCardTypeId,
					serviceFee: fee,
				};

				const legacyPostAcceptedPayment: LegacyPostAcceptedPayment = structuredClone({
					dynatouchGUID: legacyId!,
					firstCashTransaction: false,
					customerID: companyId.toString(),
					kioskID: kioskId.toString(),
					paymentGatewayExternalID: item.item.itemNumber,
					paymentDateTime: new Date().toISOString(),
					paymentAmount: item!.amountToPayWithFee!,
					paymentType: 'Card',
					paymentGUID: guid(),
					passKey: await passKeyHelper.generatePassKey(),
					cashOnes: 0,
					cashTwos: 0,
					cashFives: 0,
					cashTens: 0,
					cashTwenties: 0,
					cashFifties: 0,
					cashHundreds: 0,
					cisProvider: enumHelper.getCisConfigTypeLabel(cisType),
					paymentGateway: enumHelper.getPpConfigTypeLabel(ppType),
					cisProviderExternalID: item!.item.itemNumber,
					payorName: item!.item.customerName ?? item!.customer?.name ?? '',
					paymentReference: paymentReferenceId,
				});

				const legacyPostRejectedPayment: LegacyPostRejectedPayment = structuredClone({
					customerID: companyId.toString(),
					kioskID: kioskId.toString(),
					billpaySessionGUID: legacyId!,
					rejectedECID: '',
					rejectedReason: 'Card payment error',
					rejectedPaymentType: 'Card',
					paymentAmount: item!.amountToPayWithFee!,
					payorName: item!.item.customerName ?? item!.customer?.name ?? '',
					paymentDateTime: new Date().toISOString(),
					passKey: await passKeyHelper.generatePassKey(),
				});

				response = await api.payments.magtekData(request, legacyPostAcceptedPayment, legacyPostRejectedPayment);
				const responseData = {
					customerName: item!.item.customerName ?? item!.customer?.name ?? '',
					amountToPay: item!.amountToPay!,
					paymentId: response!.paymentId,
					cISConfig: response!.cisConfigType,
					pPConfig: response!.ppConfigType,
					referenceNumberValue: response!.referenceNumberValue,
					referenceNumberLabel: response!.referenceNumberLabel,
				};
				logger.logCardPayment.log(
					{
						...responseData,
						referenceNumberLabel: response!.referenceNumberLabel,
						cardAmount: item!.amountPaid!,
					},
					'Payment successful'
				);
				handleNavigation('payment-complete');
			} catch (error) {
				setError({ message: getString('card-read-error') });
				setErrorHint(getString('card-read-hint'));
				setCurrentState(ViewState.ERROR);
				logger.logCardPayment.error(
					{
						amountToPay: item!.amountToPayWithFee!,
						cardAmount: item!.amountToPay!,
						paymentId: response!.paymentId ?? '',
						customerName: item!.item.customerName ?? item!.customer?.name ?? '',
						cISConfig: cisType,
						pPConfig: ppType,
						referenceNumberLabel: response!.referenceNumberLabel ?? '',
						referenceNumberValue: response!.referenceNumberValue ?? '',
					},
					'Payment failed:',
					error
				);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[cartItems, handleNavigation, legacyId, transactionFee, paymentReferenceId]
	);

	useEffect(() => {
		if (magtekData && currentState === ViewState.PAY && !hasProcessed) handlePayment(magtekData);
	}, [magtekData, handlePayment, currentState, hasProcessed]);

	const handleRetry = () => {
		setError(null);
		setCurrentState(ViewState.PAY);
		setHasProcessed(false);
	};

	const handleCancel = () => {
		setError(null);
		setCurrentState(ViewState.PAY);
		setHasProcessed(false);
		handleNavigation(previousPageTarget ?? 'payment-method');
	};

	const handleServiceFeeConfirm = () => {
		setCurrentState(ViewState.PAY);
	};

	const handleServiceFeeCancel = () => {
		handleCancel();
	};

	const renderServiceFeeView = () => {
		return (
			<ServiceFeeView
				amount={cartTotalAmount}
				fee={parseAmountToNumber(transactionFee!)}
				onConfirm={handleServiceFeeConfirm}
				onCancel={handleServiceFeeCancel}
				labels={
					paymentFeeViewServerProps
						? paymentFeeViewServerProps
						: ({ titleLabelKey: 'service-fee-description-card' } as PaymentFeeViewServerProps)
				}
			/>
		);
	};

	const renderPayView = () => {
		const item = cartItems[0];
		return (
			<section className={style.section}>
				<img className={style.section__image} src={insertCardImage} alt={imageAltKey} />
				<BalancesKvpList
					className={style.section__details}
					kvpList={[]}
					totalSum={{
						label: getString(paymentTotalLabelKey ?? ''),
						value: (item?.amountToPay ?? 0) + (transactionFee ? parseAmountToNumber(transactionFee) : 0),
						forceRender: true,
					}}
				/>
				<MessageBoxSet
					className={style.section__messages}
					infoText={getString(infoBoxLabelKey ?? '')}
					warningText={getString(warningBoxLabelKey ?? '')}
				/>
			</section>
		);
	};

	const renderProcessingView = () => (
		<section className={style.processing}>
			<div className={style.processing__spinner}></div>
			<h2 className={style.processing__title}>{getString(processingPaymentLabelKey)}</h2>
		</section>
	);

	const renderErrorView = () => (
		<section className={style.error}>
			<MainMessageBox icon={AppIconType.Error} message={error?.message ?? getString(cardErrorGenericLabelKey)} />
			<Confirm
				titleLabel={getString(tryAgainMessageLabelKey)}
				yesButton={{ label: tryAgainYesLabelKey ?? getString('yes'), value: 'yes', target: '' }}
				noButton={{ label: tryAgainNoLabelKey ?? getString('no'), value: 'no', target: '' }}
				yesCallback={handleRetry}
				noCallback={handleCancel}
			/>
			<MessageBox type='warning' dangerouslySetInnerHTML={{ __html: errorHint ? errorHint : getString('card-read-hint') }} />
		</section>
	);

	const renderCurrentView = () => {
		switch (currentState) {
			case ViewState.SERVICE_FEE:
				return renderServiceFeeView();
			case ViewState.PAY:
				return renderPayView();
			case ViewState.PROCESSING:
				return renderProcessingView();
			case ViewState.ERROR:
				return renderErrorView();
			default:
				return renderPayView();
		}
	};

	return <section className={style.section}>{renderCurrentView()}</section>;
}

export default PaymentCardMagTek;
